import {
    faArrowsLeftRight, faArrowsRotate, faBell, faBook, faChartLine, faCog,
    faDatabase, faFolderMinus, faHome, faListSquares, faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import { Permissions } from '../Security/permissions';

export const navigationSections = [
    { label: 'Home', route: '/home', icon: faHome, isHome: true },
    { label: 'Dashboards', route: '/dashboards', icon: faChartLine, resourceCode: Permissions.Reports.Dashboards },
    { label: 'Data Management', icon: faDatabase, eventKey: 'data', children: [
        { label: 'Conciliation Data', route: '/conciliation/extractions', resourceCode: Permissions.Conciliation.Extractions },
        { label: 'Export to CSV', route: '/data-extraction', resourceCode: Permissions.Extractions.Manual },
        { label: 'Scheduled Tasks', route: '/scheduled-tasks', resourceCode: Permissions.Extractions.ScheduledTasks },
        { label: 'Extraction Templates', route: '/extraction-templates', resourceCode: Permissions.Extractions.Templates },
        { label: 'Execution Logs', route: '/execution-logs', resourceCode: Permissions.Extractions.ExecutionLogs },
        { label: 'API Connection', route: '/docs/api/v1', resourceCode: Permissions.DataManagement.ApiConnection }
    ] },
    { label: 'Files', icon: faFolderMinus, eventKey: 'files', children: [
        { label: 'My Documents', route: '/files', resourceCode: Permissions.Collaboration.MyDocuments },
        { label: 'Chat Files', route: '/chat-files', resourceCode: Permissions.Collaboration.ChatFiles }
    ] },
    { label: 'Agent', icon: faUserFriends, eventKey: 'agent', children: [
        { label: 'Chat', route: '/chat', resourceCode: Permissions.Collaboration.Chat },
        { label: 'DocuChat', route: '/docu-chat', resourceCode: Permissions.Collaboration.DocuChat },
        { label: 'MARCBI Chat', route: '/marcbi-chat', resourceCode: Permissions.Collaboration.MarcbiChat },
        { label: 'MARCBI Creative', route: '/marcbi-creative', resourceCode: Permissions.Collaboration.MarcbiCreative }
    ] },
    { label: 'Catalogs', icon: faBook, eventKey: 'catalogs', children: [
        { label: 'Products', route: '/product-catalog', resourceCode: Permissions.DataManagement.Products },
        { label: 'Stores', route: '/store-catalog', resourceCode: Permissions.DataManagement.Stores }
    ] },
    { label: 'Conciliation', icon: faArrowsLeftRight, eventKey: 'conciliation', children: [
        { label: 'NIT/GL Mapping', route: '/nit-gl-mapping', resourceCode: Permissions.Conciliation.NitGlMapping, companyScoped: true },
        { label: 'Card Route Mapping', route: '/card-center-route-mapping', resourceCode: Permissions.Conciliation.AccountMapping, companyScoped: true },
        { label: 'Manual Posting', route: '/manual-posting', resourceCode: Permissions.Conciliation.ManualPosting, companyScoped: true },
        { label: 'Reports', route: '/conciliation/reports', resourceCode: Permissions.Conciliation.Reports, companyScoped: true },
        { label: 'Concur Expense Processing', route: '/concur-expenses', resourceCode: Permissions.Concur.Expenses }
    ] },
    { label: 'Connections', icon: faArrowsRotate, eventKey: 'connections', children: [
        { label: 'BAC', route: '/bank-statement-configs', resourceCode: Permissions.DataManagement.BacConnection },
        { label: 'SAP', route: '/sap-company-configs', resourceCode: Permissions.DataManagement.SapConnection }
    ] },
    { label: 'Management', icon: faListSquares, eventKey: 'management', children: [
        { label: 'Companies', route: '/management/companies', resourceCode: Permissions.Management.Companies },
        { label: 'Financial Institutions', route: '/management/financial-institutions', resourceCode: Permissions.Management.FinancialInstitutions },
        { label: 'Bank Accounts', route: '/management/financial-accounts', resourceCode: Permissions.Management.FinancialAccounts },
        { label: 'Payment Cards', route: '/management/payment-cards', resourceCode: Permissions.Management.PaymentCards }
    ] }
];

export const navigationFooterSections = [
    { label: 'Admin', icon: faCog, eventKey: 'admin', children: [
        { label: 'Users', route: '/admin/users', resourceCode: Permissions.Security.Users },
        { label: 'Roles', route: '/admin/roles', resourceCode: Permissions.Security.Roles },
        { label: 'Company Access', route: '/admin/company-access', resourceCode: Permissions.Security.CompanyAccess },
        { label: 'Licenses', route: '/admin/licenses', resourceCode: Permissions.Security.Licenses },
        { label: 'Notifications', route: '/admin-notifications', resourceCode: Permissions.Collaboration.AdminNotifications },
        { label: 'Tickets', route: '/support', resourceCode: Permissions.Support.Tickets },
        { label: 'News', route: '/admin-news', resourceCode: Permissions.Collaboration.News },
        { label: 'Admin', route: '/admin-config', resourceCode: Permissions.Security.Configuration }
    ] },
    { label: 'Notifications', route: '/notifications', icon: faBell, resourceCode: Permissions.Collaboration.Notifications }
];

export const allNavigationSections = [...navigationSections, ...navigationFooterSections];
/** @type {Array<any>} */
export const navigationLeaves = [];
allNavigationSections.forEach(item => {
    if ('children' in item && item.children) navigationLeaves.push(...item.children);
    else navigationLeaves.push(item);
});
export const securedNavigationLeaves = navigationLeaves.filter(item => item.resourceCode && item.route);
export const navigationByResourceCode = new Map(securedNavigationLeaves.map(item => [item.resourceCode, item]));

export const getFirstAuthorizedRoute = ({ hasPermission }) => (
    securedNavigationLeaves.find(item => hasPermission(item.resourceCode))?.route ?? null
);

export const buildNavigationResourceTree = resources => {
    const byCode = new Map((resources || []).filter(item => item.isAssignable).map(item => [item.code, item]));
    const mappedCodes = new Set();
    const tree = allNavigationSections.map((section, sectionIndex) => {
        const leaves = (section.children || [section]).map((item, itemIndex) => {
            if (!item.resourceCode) return null;
            const resource = byCode.get(item.resourceCode);
            if (!resource) return null;
            mappedCodes.add(item.resourceCode);
            return { ...resource, name: item.label, visualRoute: item.route, displayOrder: itemIndex, children: [] };
        }).filter(Boolean);
        if (!leaves.length) return null;
        return { resourceId: `navigation-${section.eventKey || sectionIndex}`, name: section.children ? section.label : 'General',
            code: '', isActive: true, isAssignable: false, displayOrder: sectionIndex, children: leaves };
    }).filter(Boolean);
    const unmapped = (resources || []).filter(item => item.isAssignable && !mappedCodes.has(item.code));
    return { tree, unmapped };
};
